import React from 'react'
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Typography,
  FormControl,
  Select,
  Alert,
  Link
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import { Product } from 'hooks/useProduct'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import AssembleOptions from 'components/AssembleOptions'
import { validateWoodenTableRequirements } from 'utils/validateWoodenTable'

interface QuickOrderListProps {
  searchResults: Product[]
  quantities: { [key: string]: number }
  isAssembled: { [key: string]: boolean }
  hinges: { [key: string]: string }
  handleQuantityChange: (productId: string, quantity: number) => void
  handleAssembledChange: (productId: string) => void
  handleHingeChange: (productId: string, hinge: string) => void
  deleteSearchResult: (productId: string) => void
  clearAll: () => void
  addAllToCart: () => void
}

const QuickOrderList: React.FC<QuickOrderListProps> = ({
  searchResults,
  quantities,
  isAssembled,
  hinges,
  handleQuantityChange,
  handleAssembledChange,
  handleHingeChange,
  deleteSearchResult,
  clearAll,
  addAllToCart
}) => {
  const { t } = useTranslation()

  const woodenTableValidation = validateWoodenTableRequirements(
    searchResults.map(product => ({
      sku: product.sku,
      quantity: quantities[product.id] || 1
    }))
  )

  return (
    <Box>
      {searchResults.map(product => (
        <Box key={product.id} mb={5}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box display='flex' alignItems='center'>
                <Box
                  sx={{
                    width: '200px',
                    height: '200px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    component='img'
                    src={`${PRODUCT_IMAGE_BASE_URL}${product.imageIndex}.png`}
                    alt={product.sku}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={8}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Box sx={{ ml: 2, flexGrow: 1 }}>
                    <Typography
                      variant='h3'
                      sx={{
                        fontWeight: 500
                      }}
                    >
                      {product.sku}
                    </Typography>
                    <Typography
                      variant='h2'
                      sx={{
                        fontWeight: 500,
                        my: '20px'
                      }}
                    >
                      ${Number(product.price).toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <FormControl variant='outlined'>
                      <Select
                        value={quantities[product.id] || 1}
                        onChange={e =>
                          handleQuantityChange(
                            product.id,
                            Number(e.target.value)
                          )
                        }
                        sx={{
                          '.MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                          },
                          '.MuiSelect-icon': {
                            color: 'primary.main'
                          }
                        }}
                      >
                        {Array.from(
                          { length: product.qtyRestrictions },
                          (_, i) => i + 1
                        ).map(q => (
                          <MenuItem
                            key={q}
                            value={q}
                            sx={{
                              justifyContent: 'center'
                            }}
                          >
                            <Typography variant='h2'>{q}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant='h2'>
                      $
                      {(product.price * (quantities[product.id] || 1)).toFixed(
                        2
                      )}
                    </Typography>
                  </Box>

                  <Box
                    display='flex'
                    justifyContent='flex-end'
                    alignItems='center'
                    mt={1}
                  >
                    <Button
                      variant='text'
                      sx={{
                        color: 'primary.main',
                        fontFamily: 'Inter',
                        fontSize: '16px',
                        fontWeight: '400',
                        lineHeight: '22.4px',
                        textAlign: 'left'
                      }}
                      onClick={() => deleteSearchResult(product.id)}
                    >
                      {t('quickOrder.remove')}
                    </Button>
                  </Box>
                </Grid>

                {product.afmx !== 0 && (
                  <AssembleOptions
                    isHingeable={product.isHingeable}
                    isAssembled={isAssembled[product.id] || false}
                    hingeDirection={hinges[product.id] || 'LEFT'}
                    onUpdate={({ isAssembled, hingeDirection }) => {
                      if (isAssembled !== undefined)
                        handleAssembledChange(product.id)
                      if (hingeDirection !== undefined)
                        handleHingeChange(product.id, hingeDirection)
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ width: '100%', mt: 2 }} />
        </Box>
      ))}

      {woodenTableValidation.warning && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {woodenTableValidation.messageParts ? (
            <>
              {woodenTableValidation.messageParts.beforeLink}
              <Link
                href='/search?keyword=-ber'
                sx={{
                  color: 'error.main',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                {woodenTableValidation.messageParts.linkText}
              </Link>
              {woodenTableValidation.messageParts.afterLink}
            </>
          ) : (
            woodenTableValidation.warning
          )}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          gap: '10px'
        }}
      >
        <Button
          variant='outlined'
          sx={{
            minWidth: '150px',
            minHeight: '40px',
            borderRadius: '4px',
            color: 'primary.main'
          }}
          onClick={clearAll}
        >
          {t('quickOrder.clearAll')}
        </Button>

        <Button
          variant='contained'
          sx={{
            minWidth: '150px',
            minHeight: '40px',
            gap: '10px',
            borderRadius: '4px'
          }}
          onClick={addAllToCart}
          disabled={
            searchResults.length === 0 || !woodenTableValidation.isValid
          }
        >
          <ShoppingBagIcon />
          {t('quickOrder.addToCart')}
        </Button>
      </Box>
    </Box>
  )
}

export default QuickOrderList
